import React, { useState, useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import { streamChatCompletion } from '../../utils/openaiClient';
import './ChatSidebar.css';

const ChatSidebar = ({ 
  isOpen, 
  onClose, 
  initialSystemPrompt, 
  initialUserPrompt,
  title = 'AI Assistant',
  onExport,
  exportOptions = { pdf: true, markdown: true, copy: true }
}) => {
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [streamingContent, setStreamingContent] = useState('');
  const messagesEndRef = useRef(null);
  const sidebarRef = useRef(null);

  // Initialize chat with provided prompts
  useEffect(() => {
    if (isOpen && initialSystemPrompt && initialUserPrompt && messages.length === 0) {
      handleInitialPrompt();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialSystemPrompt, initialUserPrompt]);

  // Scroll to bottom when messages update
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, streamingContent]);

  // Handle clicks outside sidebar
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (sidebarRef.current && !sidebarRef.current.contains(event.target)) {
        const isClickOnTriggerButton = event.target.closest('.generate-button');
        if (!isClickOnTriggerButton) {
          onClose();
        }
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  const handleInitialPrompt = async () => {
    setIsLoading(true);
    setStreamingContent('');
    
    // Add thinking message
    const thinkingMessage = {
      id: Date.now(),
      role: 'assistant',
      content: 'Generating your content...',
      timestamp: new Date(),
      isThinking: true
    };
    
    setMessages([thinkingMessage]);
    
    try {
      let fullResponse = '';
      await streamChatCompletion(
        initialUserPrompt,
        (chunk, fullContent) => {
          fullResponse = fullContent;
          setStreamingContent(fullContent);
        },
        {
          systemPrompt: initialSystemPrompt,
          temperature: 0.7,
          maxTokens: 1000
        }
      );
      
      // Replace thinking message with actual response
      setMessages([{
        id: Date.now(),
        role: 'assistant',
        content: fullResponse,
        timestamp: new Date()
      }]);
      
      setStreamingContent('');
    } catch (error) {
      console.error('Error generating initial response:', error);
      setMessages([{
        id: Date.now(),
        role: 'assistant',
        content: 'Sorry, I encountered an error generating the content. Please try again.',
        timestamp: new Date(),
        isError: true
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;
    
    const userMessage = {
      id: Date.now(),
      role: 'user',
      content: inputValue,
      timestamp: new Date()
    };
    
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);
    setStreamingContent('');
    
    // Add thinking message
    const thinkingMessage = {
      id: Date.now() + 1,
      role: 'assistant',
      content: 'Thinking...',
      timestamp: new Date(),
      isThinking: true
    };
    
    setMessages(prev => [...prev, thinkingMessage]);
    
    try {
      // Build conversation with context
      const contextPrompt = messages.length > 0 
        ? `Previous conversation:\n${messages.filter(msg => !msg.isThinking && !msg.isError).map(msg => `${msg.role === 'user' ? 'User' : 'Assistant'}: ${msg.content}`).join('\n\n')}\n\nUser: ${inputValue}`
        : inputValue;
      
      let fullResponse = '';
      await streamChatCompletion(
        contextPrompt,
        (chunk, fullContent) => {
          fullResponse = fullContent;
          setStreamingContent(fullContent);
        },
        {
          systemPrompt: initialSystemPrompt || 'You are a helpful AI assistant.',
          temperature: 0.7,
          maxTokens: 1000
        }
      );
      
      // Replace thinking message with actual response
      setMessages(prev => {
        const newMessages = [...prev];
        newMessages[newMessages.length - 1] = {
          id: Date.now() + 2,
          role: 'assistant',
          content: fullResponse,
          timestamp: new Date()
        };
        return newMessages;
      });
      
      setStreamingContent('');
    } catch (error) {
      console.error('Error sending message:', error);
      setMessages(prev => {
        const newMessages = [...prev];
        newMessages[newMessages.length - 1] = {
          id: Date.now() + 3,
          role: 'assistant',
          content: 'Sorry, I encountered an error. Please try again.',
          timestamp: new Date(),
          isError: true
        };
        return newMessages;
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleExport = (format, content) => {
    if (onExport) {
      onExport(format, content);
    } else {
      // Default export behavior
      switch (format) {
        case 'copy':
          navigator.clipboard.writeText(content);
          break;
        case 'markdown':
          downloadMarkdown(content);
          break;
        case 'pdf':
          // PDF export would be handled by parent component
          break;
        default:
          break;
      }
    }
  };

  const downloadMarkdown = (content) => {
    const blob = new Blob([content], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${title.toLowerCase().replace(/\s+/g, '-')}-${new Date().toISOString().split('T')[0]}.md`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const getLatestAssistantMessage = () => {
    const assistantMessages = messages.filter(
      msg => msg.role === 'assistant' && !msg.isThinking && !msg.isError
    );
    return assistantMessages[assistantMessages.length - 1]?.content || streamingContent;
  };

  return (
    <>
      {/* Overlay */}
      <div className={`chat-sidebar-overlay ${isOpen ? 'open' : ''}`} />
      
      {/* Sidebar */}
      <div ref={sidebarRef} className={`chat-sidebar ${isOpen ? 'open' : ''}`}>
        <div className="chat-sidebar-header">
          <h2>{title}</h2>
          <button className="close-button" onClick={onClose}>
            ×
          </button>
        </div>
        
        <div className="chat-sidebar-messages">
          {messages.map((message) => (
            <div key={message.id} className={`chat-message ${message.role} ${message.isError ? 'error' : ''}`}>
              <div className="message-header">
                <span className="message-role">
                  {message.role === 'user' ? '👤 You' : '🤖 Assistant'}
                </span>
                <span className="message-time">
                  {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>
              <div className="message-content">
                {message.isThinking ? (
                  <div className="thinking-indicator">
                    <span className="thinking-dot"></span>
                    <span className="thinking-dot"></span>
                    <span className="thinking-dot"></span>
                  </div>
                ) : (
                  <ReactMarkdown>{message.content}</ReactMarkdown>
                )}
              </div>
            </div>
          ))}
          
          {streamingContent && (
            <div className="chat-message assistant streaming">
              <div className="message-header">
                <span className="message-role">🤖 Assistant</span>
                <span className="message-time">now</span>
              </div>
              <div className="message-content">
                <ReactMarkdown>{streamingContent}</ReactMarkdown>
                <span className="streaming-cursor"></span>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>
        
        <div className="chat-sidebar-actions">
          {getLatestAssistantMessage() && (
            <div className="export-actions">
              {exportOptions.copy && (
                <button
                  className="export-button copy"
                  onClick={() => handleExport('copy', getLatestAssistantMessage())}
                  title="Copy to clipboard"
                >
                  📋
                </button>
              )}
              {exportOptions.markdown && (
                <button
                  className="export-button markdown"
                  onClick={() => handleExport('markdown', getLatestAssistantMessage())}
                  title="Download as Markdown"
                >
                  📝
                </button>
              )}
              {exportOptions.pdf && (
                <button
                  className="export-button pdf"
                  onClick={() => handleExport('pdf', getLatestAssistantMessage())}
                  title="Download as PDF"
                >
                  📄
                </button>
              )}
            </div>
          )}
          
          <div className="chat-input-container">
            <textarea
              className="chat-input"
              placeholder="Ask for changes or improvements..."
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSendMessage();
                }
              }}
              disabled={isLoading}
              rows="2"
            />
            <button
              className="send-button"
              onClick={handleSendMessage}
              disabled={!inputValue.trim() || isLoading}
            >
              {isLoading ? '⏳' : '📤'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default ChatSidebar;